import type { ApexOptions } from 'apexcharts'
import type { ChartCard } from '@/features/dashboard/api/schemas'
import type { Theme } from '@/shared/theme/ThemeContext'
import { niceAxis, type Axis } from '@/features/dashboard/format/axis'
import {
  AXIS_FONT_SIZE,
  FONT,
  sliceColor,
  valueLabelStyle,
  type ChartPalette,
} from './chartTheme'
import {
  axisWithTicks,
  compactValue,
  labelStride,
  plotWidth,
  shortMonth,
  tick,
} from './deepDiveAxis'
import {
  secondaryFormat,
  seriesType,
  type DeepDiveModel,
  type Row,
} from './deepDiveModel'
import { donutOptions, waterfallOptions } from './categoryOptions'
import { KNOWN_REFERENCES, referenceColorFor } from './series'
import { seriesTooltipHtml } from './tooltipHtml'

/** Width of one value above a column, in px, and of a total above a stack. */
const VALUE_LABEL_PX = 44
const TOTAL_LABEL_PX = 56

type Opt<K extends keyof ApexOptions> = NonNullable<ApexOptions[K]>

type Colors = { metrics: string[]; references: string[] }

const deepDiveColors = (
  model: DeepDiveModel,
  palette: ChartPalette
): Colors => ({
  metrics: model.metrics.map((row, i) =>
    // The ratio line of a dual-axis chart is drawn in the text colour.
    model.dual && (row.axis === 'secondary' || row.render === 'line')
      ? palette.text
      : sliceColor(palette, i)
  ),
  // A named benchmark has its colour (target, budget...); another series
  // drawn dashed (a peer group) takes the quiet text colour, then the ramp.
  references: model.references.map((row, i) => {
    const key = row.key ?? ''
    return KNOWN_REFERENCES.has(key)
      ? referenceColorFor(key, palette, i)
      : i === 0
        ? palette.textAlt
        : sliceColor(palette, model.metrics.length + i)
  }),
})

/** A line's axis starts a little under its lowest value, but never below zero for values above it. */
const paddedLow = (lo: number, hi: number) =>
  lo >= 0 ? Math.max(0, lo - (hi - lo) * 0.05) : lo - (hi - lo) * 0.05

/** A series read on the right-hand axis says so in the tooltip. */
const rightAxis = (row: Row) =>
  row.axis === 'secondary' ? `${row.name} (right axis)` : row.name

const isLineChart = ({ kind, stacked }: DeepDiveModel) =>
  kind === 'line' || (kind === 'area' && !stacked)

const dataValues = (rows: Row[]) =>
  rows.flatMap(row =>
    row.data.filter((v): v is number => typeof v === 'number')
  )

/** The value axis (from zero for columns and areas, round steps, few ticks) and the right-hand one of a dual chart. */
const valueAxes = (model: DeepDiveModel, headroom: boolean) => {
  const { metrics, references, stacked, percent, dual } = model
  const columnRows = dual
    ? metrics.filter(row => row.axis !== 'secondary' && row.render !== 'line')
    : metrics
  const stackedTops = stacked && !percent ? model.totals : []
  const primaryValues = [
    ...(stacked ? stackedTops : dataValues(columnRows)),
    ...dataValues(dual ? [] : references),
  ]
  const lineOnly = isLineChart(model) && !dual
  const lo = lineOnly
    ? Math.min(...primaryValues)
    : Math.min(0, ...primaryValues)
  const hi = Math.max(...primaryValues, lo + 1e-9)
  const room = headroom ? (hi - lo) * 0.08 : 0
  const primary: Axis = percent
    ? { min: 0, max: 100, ticks: 4 }
    : primaryValues.length === 0
      ? niceAxis(0, 1)
      : niceAxis(
          lineOnly ? paddedLow(lo, hi) : lo,
          hi + room + (lineOnly ? (hi - lo) * 0.08 : 0)
        )
  const secondaryValues = dataValues(
    [...metrics, ...references].filter(row => row.axis === 'secondary')
  )
  const low = Math.min(...secondaryValues)
  const high = Math.max(...secondaryValues)
  const pad = (high - low) * 0.1
  const secondary =
    secondaryValues.length > 0
      ? axisWithTicks(low - pad, high + pad, primary.ticks)
      : primary
  return { primary, secondary }
}

const yAxisOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  axes: ReturnType<typeof valueAxes>
): Opt<'yaxis'> => {
  const labelStyle = { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE }
  if (!model.dual) {
    const { primary } = axes
    return {
      min: primary.min,
      max: primary.max,
      tickAmount: primary.ticks,
      // The values are read at the chart's right edge, as on the home card.
      opposite: true,
      labels: {
        style: labelStyle,
        formatter: (v: number) =>
          model.percent ? `${Math.round(v)}%` : tick(v, card.unit, card.format),
      },
    }
  }
  // One entry per series; the series of an axis share its first one.
  const drawn = [...model.metrics, ...model.references]
  return drawn.map(row => {
    const secondary = row.axis === 'secondary'
    const first =
      drawn.find(other => (other.axis === 'secondary') === secondary) ?? row
    const own = secondary ? axes.secondary : axes.primary
    return {
      seriesName: first.name,
      opposite: secondary,
      show: first === row,
      min: own.min,
      max: own.max,
      tickAmount: own.ticks,
      labels: {
        style: labelStyle,
        formatter: (v: number) =>
          secondary
            ? tick(v, null, secondaryFormat(card))
            : tick(v, card.unit, card.format),
      },
    }
  })
}

/** The latest value ends a line: a filled dot, and its value beside it. */
type LineEnd = { index: number; at: number; color: string }

/** The ends of up to three lines: more would crowd. */
const lineEnds = (
  model: DeepDiveModel,
  colors: string[],
  palette: ChartPalette
): LineEnd[] => {
  const drawn = [...model.metrics, ...model.references]
  const lines = drawn
    .map((row, index) => ({ row, index }))
    .filter(({ row }) =>
      model.dual
        ? seriesType(model, row) === 'line'
        : isLineChart(model) || model.references.includes(row)
    )
  if (lines.length > 3) return []
  return lines.flatMap(({ row, index }) => {
    const at = row.data.reduce<number>(
      (last, v, i) => (v === null ? last : i),
      -1
    )
    return at < 0 ? [] : [{ index, at, color: colors[index] ?? palette.goal }]
  })
}

const dataLabelOptions = (
  card: ChartCard,
  palette: ChartPalette,
  ends: LineEnd[],
  showsEnds: boolean,
  roomForValues: boolean
): Opt<'dataLabels'> => {
  if (showsEnds) {
    return {
      // Only a line's latest point carries its value, to the right of its dot.
      enabled: true,
      enabledOnSeries: ends.map(end => end.index),
      textAnchor: 'start',
      offsetX: 8,
      offsetY: 0,
      ...valueLabelStyle(palette),
      formatter: (
        v: string | number | number[],
        opts?: { seriesIndex?: number; dataPointIndex?: number }
      ) => {
        const end = ends.find(
          candidate => candidate.index === opts?.seriesIndex
        )
        return end?.at === opts?.dataPointIndex && typeof v === 'number'
          ? compactValue(v, card.unit, card.format)
          : ''
      },
    }
  }
  if (!roomForValues) return { enabled: false }
  return {
    enabled: true,
    offsetY: -16,
    ...valueLabelStyle(palette),
    formatter: (v: string | number | number[]) =>
      typeof v === 'number' && v !== 0
        ? compactValue(v, card.unit, card.format)
        : '',
  }
}

/** The total above each stack, while they fit; past that, the latest alone. */
const totalPoints = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  everywhere: boolean
) =>
  model.showTotals
    ? model.labels.flatMap((label, i) =>
        everywhere || i === model.labels.length - 1
          ? [
              {
                x: label,
                y: model.totals[i] ?? 0,
                marker: { size: 0, strokeWidth: 0, radius: 0 },
                label: {
                  text: compactValue(
                    model.totals[i] ?? 0,
                    card.unit,
                    card.format
                  ),
                  borderWidth: 0,
                  offsetY: -6,
                  style: {
                    fontSize: AXIS_FONT_SIZE,
                    fontWeight: 500,
                    color: palette.textAlt,
                    background: 'transparent',
                  },
                },
              },
            ]
          : []
      )
    : []

const tooltipOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  theme: Theme,
  colors: Colors
): Opt<'tooltip'> => {
  const { stacked, percent, references, labels } = model
  // Each category's share of its total, for the 100% columns.
  const share = (v: number, i: number) => {
    const total = model.totals[i] ?? 0
    return `${total > 0 ? Math.round((v / total) * 100) : 0}%`
  }
  return {
    theme,
    shared: true,
    intersect: false,
    custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
      const metricRows = model.metrics.flatMap((row, i) => {
        const v = row.data[dataPointIndex]
        // A stack lists only what it holds at that category.
        return v === null || v === undefined || (stacked && !percent && v === 0)
          ? []
          : [
              {
                // A lone metric is the card's own; naming it adds nothing.
                ...(model.metrics.length > 1 && { label: rightAxis(row) }),
                value: v,
                color: colors.metrics[i] ?? palette.goal,
                ...(percent && { suffix: share(v, dataPointIndex) }),
                ...(row.axis === 'secondary' && {
                  format: secondaryFormat(card),
                }),
              },
            ]
      })
      const referenceRows = references.flatMap((row, i) => {
        const v = row.data[dataPointIndex]
        return v === null || v === undefined
          ? []
          : [
              {
                label: rightAxis(row),
                value: v,
                color: colors.references[i] ?? palette.goal,
                dashed: true,
                ...(row.axis === 'secondary' && {
                  format: secondaryFormat(card),
                }),
              },
            ]
      })
      return seriesTooltipHtml({
        label: labels[dataPointIndex] ?? '',
        unit: card.unit,
        format: card.format,
        palette,
        // A stack reads top to bottom, as it is drawn.
        rows: [
          ...(stacked ? metricRows.reverse() : metricRows),
          ...referenceRows,
        ],
        ...(model.showTotals && { total: model.totals[dataPointIndex] ?? 0 }),
      })
    },
  }
}

export const deepDiveOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  theme: Theme,
  height: number,
  width = 0
): ApexOptions => {
  if (model.kind === 'waterfall') {
    return waterfallOptions(card, model, palette, theme, height, width)
  }
  if (model.kind === 'pie') {
    return donutOptions(card, model, palette, theme, height)
  }
  const { kind, stacked, references, labels, percent, dual } = model
  const isBar = kind === 'bar'
  const drawn = [...model.metrics, ...references]
  const deepColors = deepDiveColors(model, palette)
  // Listed as the series are drawn: reversed where the areas are.
  const colors = [
    ...(model.stacksItself
      ? [...deepColors.metrics].reverse()
      : deepColors.metrics),
    ...deepColors.references,
  ]
  const plot = plotWidth(width)
  const count = labels.length
  const stride = labelStride(count, width)

  // A lone bar series carries its value above each column while they are few
  // enough to read; stacks carry a total, never a label per segment.
  const singleBars =
    kind === 'bar' && !stacked && !dual && model.metrics.length === 1
  const roomForValues = singleBars && count * VALUE_LABEL_PX <= plot
  const headroom = singleBars || model.showTotals

  const axes = valueAxes(model, headroom)
  const ends = lineEnds(model, colors, palette)
  // A line's end value sits right of its dot, so a dual chart (axis there) has none.
  const showsEnds = !dual && ends.length > 0

  return {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      fontFamily: FONT,
      foreColor: palette.textAlt,
      type: model.apexType,
      height,
      stacked: stacked && !model.stacksItself,
      ...(percent && { stackType: '100%' as const }),
      zoom: { enabled: false },
      animations: { speed: 300 },
    },
    colors: singleBars
      ? [
          ({ dataPointIndex }: { dataPointIndex: number }) =>
            dataPointIndex === count - 1
              ? (palette.categorical[0] ?? palette.goal)
              : palette.bar,
        ]
      : colors,
    theme: { mode: theme },
    states: { hover: { filter: { type: 'none' } } },
    grid: {
      borderColor: palette.barGrid,
      strokeDashArray: 6,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: {
        top: headroom ? 18 : 4,
        right: showsEnds ? 48 : 12,
        left: isLineChart(model) ? 24 : 8,
        bottom: 0,
      },
    },
    legend: { show: false },
    dataLabels: dataLabelOptions(card, palette, ends, showsEnds, roomForValues),
    plotOptions: {
      bar: {
        borderRadius: 3,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        columnWidth: stacked || singleBars ? '72%' : '64%',
        dataLabels: { position: 'top' },
      },
    },
    stroke:
      references.length > 0 || dual
        ? {
            curve: 'straight',
            width: [
              ...model.metrics.map(row =>
                (dual ? seriesType(model, row) === 'column' : isBar) ? 0 : 2
              ),
              ...references.map(() => 2),
            ],
            dashArray: [
              ...model.metrics.map(() => 0),
              ...references.map(() => 5),
            ],
          }
        : isBar
          ? stacked
            ? // A hairline in the card's colour parts the segments of a stack.
              { show: true, width: 1, colors: [palette.card] }
            : { show: false }
          : { curve: 'straight', width: 2 },
    // A lone trend area fades toward the baseline; stacked bands keep a flat
    // opacity, since a gradient would fade them by the chart, not by the band.
    fill:
      kind === 'area'
        ? stacked
          ? { type: 'solid', opacity: theme === 'dark' ? 0.4 : 0.1 }
          : {
              type: 'gradient',
              gradient: {
                shadeIntensity: 0,
                opacityFrom: model.metrics.length === 1 ? 0.18 : 0.1,
                opacityTo: 0,
                stops: [0, 90, 100],
              },
            }
        : dual
          ? {
              opacity: drawn.map(row =>
                seriesType(model, row) === 'column' ? 0.7 : 1
              ),
            }
          : { opacity: 1 },
    markers: {
      size: dual
        ? drawn.map(row => (seriesType(model, row) === 'line' ? 3 : 0))
        : 0,
      ...(dual && {
        colors: drawn.map(() => palette.card),
        strokeColors: colors,
        strokeWidth: 2,
      }),
      hover: { size: 5 },
      discrete: ends.map(end => ({
        seriesIndex: end.index,
        dataPointIndex: end.at,
        fillColor: end.color,
        strokeColor: end.color,
        size: 4,
        shape: 'circle' as const,
      })),
    },
    xaxis: {
      categories: labels,
      labels: {
        rotate: 0,
        // Thinned here, counted back from the latest category: Apex's own
        // check drops them unevenly.
        hideOverlappingLabels: false,
        trim: false,
        formatter: (text: string | number, _timestamp, opts) =>
          typeof text !== 'string' ||
          (typeof opts?.i === 'number' && (count - 1 - opts.i) % stride !== 0)
            ? ''
            : shortMonth(text),
        style: { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE },
      },
      axisBorder: { color: palette.baseline },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      crosshairs: { show: false },
    },
    yaxis: yAxisOptions(card, model, palette, axes),
    annotations: {
      ...(axes.primary.min < 0 && axes.primary.max > 0
        ? {
            yaxis: [
              { y: 0, borderColor: palette.baseline, strokeDashArray: 0 },
            ],
          }
        : {}),
      points: totalPoints(card, model, palette, count * TOTAL_LABEL_PX <= plot),
    },
    tooltip: tooltipOptions(card, model, palette, theme, deepColors),
  }
}
